import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import "./App.css";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import MobileMenuSections from "./components/MobileMenuSections";
import Interactive3DShowcase from "./components/Interactive3DShowcase";
import Mobile3DGallery from "./components/Mobile3DGallery";
import FeaturedMapsScroll from "./components/FeaturedMapsScroll";
import Filters from "./components/Filters";
import MapGallery from "./components/MapGallery";
import Footer from "./components/Footer";
import BackToTop from "./components/BackToTop";
import MapModal from "./components/MapModal";
import SidePanel from "./components/SidePanel";
import CategoryView from "./components/CategoryView";
import TexteAntice from "./pages/TexteAntice";
import TermeniConditii from "./pages/TermeniConditii";
import PoliticaConfidentialitate from "./pages/PoliticaConfidentialitate";
import GlobalAudioPlayer from "./components/GlobalAudioPlayer";
import { AudioProvider } from "./context/AudioContext";
import { fetchMapsLightweight, fetchMapById } from "./services/api";
import ErrorBoundary from "./components/ErrorBoundary";
import { AudioLoadingFallback, MapLoadingFallback } from "./components/LoadingFallbacks";
import SEOHead from "./components/SEOHead";

// Lazy load heavy components
const InteractiveMap = lazy(() => import("./components/InteractiveMap"));
const AudioSummaries = lazy(() => import("./components/AudioSummaries"));

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [yearRange, setYearRange] = useState([150, 2026]); // Default wide range from 150 D.Hr.
  const [showExplorer, setShowExplorer] = useState(false);
  const [showAudioSummaries, setShowAudioSummaries] = useState(false);
  const [allMaps, setAllMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null); // For MapModal from Explorer
  
  // SIDE PANEL STATE - Global state pentru drawer
  const [activeMap, setActiveMap] = useState(null); // Harta selectată în Side Panel
  
  // Set default view mode based on screen size
  // Mobile (< 768px) = list, Desktop = grid
  const getInitialViewMode = () => {
    return window.innerWidth < 768 ? 'list' : 'grid';
  };
  
  const [viewMode, setViewMode] = useState(getInitialViewMode());

  // Load all maps for the explorer (lightweight data)
  useEffect(() => {
    const loadMaps = async () => {
      try {
        const data = await fetchMapsLightweight();
        setAllMaps(data);
      } catch (error) {
        console.error('Error loading maps:', error);
      }
    };
    loadMaps();
  }, []);

  // Check for pending map modal from Mobile3DGallery
  useEffect(() => {
    const pendingMap = sessionStorage.getItem('openMapModal');
    if (pendingMap) {
      try {
        const mapData = JSON.parse(pendingMap);
        // Deep clone to ensure new reference and prevent stale closure
        if (mapData && mapData.id && (mapData.title || mapData.description)) {
          setSelectedMap({ ...mapData });
        }
        sessionStorage.removeItem('openMapModal');
      } catch (e) {
        console.error('Error parsing pending map:', e);
        sessionStorage.removeItem('openMapModal');
      }
    }
  }, []);

  const handleResetFilters = () => {
    setSearchQuery('');
    setRegionFilter('all');
    setYearRange([1400, 2000]);
  };

  const [openedFromExplorer, setOpenedFromExplorer] = useState(false);

  // SIDE PANEL HANDLERS
  const handleMapMarkerClick = useCallback(async (mapData) => {
    // First set lightweight data for immediate display
    setActiveMap(mapData);
    // Then fetch full description
    try {
      const fullMapData = await fetchMapById(mapData.id);
      if (fullMapData) {
        setActiveMap(fullMapData);
      }
    } catch (error) {
      console.error('Error fetching full map data:', error);
    }
  }, []);

  const handleCloseSidePanel = useCallback(() => {
    setActiveMap(null);
  }, []);

  // Handler for selecting a map from Explorer (now opens side panel, not modal)
  const handleSelectMapFromExplorer = async (map) => {
    // First set lightweight data for immediate display
    setActiveMap(map);
    // Then fetch full description
    try {
      const fullMapData = await fetchMapById(map.id);
      if (fullMapData) {
        setActiveMap(fullMapData);
      }
    } catch (error) {
      console.error('Error fetching full map data:', error);
    }
  };

  // Handler for closing modal - return to Explorer if opened from there
  const handleCloseModal = () => {
    if (openedFromExplorer) {
      setShowExplorer(true);     // Reopen Explorer
      setOpenedFromExplorer(false);
    }
    setSelectedMap(null);        // Close modal
  };

  // Navigation in modal - fetch full details with deep clone to prevent stale closures
  const handleNextMap = async () => {
    if (!selectedMap || allMaps.length === 0) return;
    const currentIndex = allMaps.findIndex(m => m.id === selectedMap.id);
    const nextIndex = (currentIndex + 1) % allMaps.length;
    const nextMap = allMaps[nextIndex];
    try {
      const fullData = await fetchMapById(nextMap.id);
      // Deep clone to ensure new reference
      setSelectedMap({ ...fullData });
    } catch (err) {
      // Fallback with deep clone
      setSelectedMap({ ...nextMap });
    }
  };

  const handlePrevMap = async () => {
    if (!selectedMap || allMaps.length === 0) return;
    const currentIndex = allMaps.findIndex(m => m.id === selectedMap.id);
    const prevIndex = (currentIndex - 1 + allMaps.length) % allMaps.length;
    const prevMap = allMaps[prevIndex];
    try {
      const fullData = await fetchMapById(prevMap.id);
      // Deep clone to ensure new reference
      setSelectedMap({ ...fullData });
    } catch (err) {
      // Fallback with deep clone
      setSelectedMap({ ...prevMap });
    }
  };

  return (
    <AudioProvider>
      <Routes>
        {/* Category View Page */}
        <Route path="/category/:regionId" element={<CategoryView />} />
        
        {/* Cronici Istorice Page */}
        <Route path="/texte-antice" element={<TexteAntice />} />
        
        {/* Legal Pages - Accesibile din Footer */}
        <Route path="/termeni-conditii" element={<TermeniConditii />} />
        <Route path="/politica-confidentialitate" element={<PoliticaConfidentialitate />} />
        
        {/* Mobile 3D Gallery Page - wrapped to set background immediately */}
        <Route path="/galerie-3d" element={
          <div style={{ backgroundColor: '#1a1a1a', minHeight: '100vh' }}>
            <Mobile3DGallery onMapClick={(map) => setSelectedMap(map)} />
          </div>
        } />
        
        {/* Home Page */}
        <Route path="*" element={
          <div className="App min-h-screen bg-gray-50">
            <SEOHead
              title="Casa Cartarum — Galerie Interactivă de Hărți Istorice ale României"
              description="Descoperă peste 80 de hărți istorice ale României într-o galerie interactivă 3D. Explorează istoria cartografiei românești de la 1500 până astăzi. Colecție rară digitizată."
              canonical="https://www.casacartarum.ro/"
            />
            {/* Interactive Map Explorer - Full Screen Overlay with Side Panel */}
            {showExplorer && (
              <ErrorBoundary title="Eroare la încărcarea hărții" message="Harta interactivă nu a putut fi încărcată. Vă rugăm să încercați din nou.">
                <Suspense fallback={<MapLoadingFallback />}>
                  <div className="explorer-layout" data-testid="explorer-layout">
                    {/* Side Panel Drawer - Left Side */}
                    <div 
                      className={`side-panel-drawer ${activeMap ? 'open' : 'closed'}`}
                      data-testid="side-panel-drawer"
                    >
                      <SidePanel 
                        activeMap={activeMap}
                        onClose={handleCloseSidePanel}
                      />
                    </div>
                    
                    {/* Map Container - Takes remaining space */}
                    <div className="map-container">
                      <InteractiveMap 
                        onClose={() => {
                          setShowExplorer(false);
                          setActiveMap(null); // Close side panel when exiting explorer
                        }} 
                        mapsData={allMaps}
                        onSelectMap={handleSelectMapFromExplorer}
                        onMarkerClick={handleMapMarkerClick}
                        activeMapId={activeMap?.id}
                      />
                    </div>
                  </div>
                </Suspense>
              </ErrorBoundary>
            )}

      <Navbar 
        onOpenExplorer={() => {
          setShowExplorer(true);
        }} 
        onOpenAudio={() => {
          setShowAudioSummaries(true);
        }}
      />
      <Hero onOpenExplorer={() => {
        setShowExplorer(true);
      }} />
      
      {/* Mobile Menu Sections - Quick access to all pages (doar mobil) */}
      <MobileMenuSections 
        onOpenExplorer={() => setShowExplorer(true)}
        onOpenAudio={() => setShowAudioSummaries(true)}
      />
      
      {/* Featured Maps Scroll - DOAR PE MOBIL (< 1024px) */}
      <FeaturedMapsScroll onMapClick={(map) => setSelectedMap(map)} />
      
      {/* Interactive 3D Showcase Section - linked to Transsylvania XXI Nova Tabula */}
      <Interactive3DShowcase onMapClick={(map) => setSelectedMap(map)} />
      
      {/* Anchor point for Galerie navigation */}
      <div id="galerie"></div>
      <Filters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        yearRange={yearRange}
        setYearRange={setYearRange}
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onReset={handleResetFilters}
      />
      <MapGallery
        searchQuery={searchQuery}
        yearRange={yearRange}
        regionFilter={regionFilter}
        viewMode={viewMode}
      />
      <Footer />
      <BackToTop />

      {/* Explorer Layout Styles */}
      <style>{`
        .explorer-layout {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: grid;
          grid-template-columns: auto 1fr;
        }

        .side-panel-drawer {
          width: 0;
          height: 100vh;
          overflow: hidden;
          background: #ffffff;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
          transition: width 0.3s ease-in-out;
          z-index: 10;
        }

        .side-panel-drawer.open {
          width: 380px;
        }

        .side-panel-drawer.closed {
          width: 0;
        }

        .map-container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        /* Mobile responsive - Side Panel takes full screen */
        @media (max-width: 768px) {
          .explorer-layout {
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
          }

          .side-panel-drawer {
            position: fixed;
            left: 0;
            top: 0;
            bottom: 0;
            right: 0;
            z-index: 9999;
            max-width: none;
          }

          .side-panel-drawer.open {
            width: 100%;
            pointer-events: auto;
            opacity: 1;
          }

          .side-panel-drawer.closed {
            width: 0;
            pointer-events: none;
            opacity: 0;
          }

          /* Ascunde markerii când Side Panel e deschis pe mobil */
          .side-panel-drawer.open ~ .map-container .maplibregl-marker {
            display: none !important;
          }

          .map-container {
            grid-row: 1;
            grid-column: 1;
          }
        }
      `}</style>
          </div>
        } />
      </Routes>

      {/* Map Modal - GLOBAL - opened from any page */}
      {/* Strict truthiness evaluation to prevent race conditions */}
      {selectedMap && selectedMap.id && (selectedMap.description || selectedMap.fullDescription || selectedMap.title) && (
        <MapModal
          map={selectedMap}
          onClose={handleCloseModal}
          onNext={handleNextMap}
          onPrev={handlePrevMap}
        />
      )}

      {/* Audio Summaries Page - GLOBAL overlay */}
      {showAudioSummaries && (
        <ErrorBoundary title="Eroare la încărcarea arhivelor" message="Arhivele audio nu au putut fi încărcate. Vă rugăm să încercați din nou.">
          <Suspense fallback={<AudioLoadingFallback />}>
            <AudioSummaries 
              onClose={() => setShowAudioSummaries(false)} 
            />
          </Suspense>
        </ErrorBoundary>
      )}
      
      {/* Global Audio Player - persists across all pages */}
      <GlobalAudioPlayer />
    </AudioProvider>
  );
}

export default App;
